import { Link } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { usePerms } from '@/lib/auth'
import { CloudOff, RefreshCw } from 'lucide-react'
import { useDepartments, useServices } from '@/lib/hooks'
import { useOffline } from '@/lib/offline/context'
import type { VisitDraft } from '@/lib/offline/outbox'
import { Input, Select } from '@/components/ui'

/** Shown under the top bar while offline or while this user has records waiting on this device. */
export function OfflineBanner() {
  const { online, mine, othersPending, syncing, syncNow } = useOffline()
  // Keep the reference lists used by offline registration warm while the connection is up
  // (same query keys as useDepartments/useServices; data is non-clinical).
  const { canAny } = usePerms()
  const frontDesk = canAny('patients.write', 'appointments.manage')
  useQuery({ queryKey: ['departments'], queryFn: () => api.get('/departments'), enabled: frontDesk, staleTime: 5 * 60_000 })
  useQuery({ queryKey: ['services', 'all'], queryFn: () => api.get('/services', {}), enabled: frontDesk, staleTime: 5 * 60_000 })
  const waiting = mine.filter((i) => i.status === 'pending' || i.status === 'syncing').length
  const attention = mine.filter((i) => i.status === 'needs_review' || i.status === 'failed').length
  if (online && waiting === 0 && attention === 0) return null
  return (
    <div role="status" className={`no-print flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-2 text-[13px] font-medium ${online ? 'bg-brand-50 text-brand-900' : 'bg-amber-100 text-amber-950'}`}>
      {!online && (
        <span className="flex items-center gap-1.5">
          <CloudOff className="h-4 w-4" /> Offline — new registrations and walk-ins are saved on this device and sent when the connection returns.
        </span>
      )}
      {waiting > 0 && <span>{waiting} waiting to send{syncing ? ' (sending…)' : ''}</span>}
      {attention > 0 && <span className="font-semibold text-rose-800">{attention} need attention</span>}
      {othersPending > 0 && <span className="text-ink-600">({othersPending} from other users on this device)</span>}
      {online && waiting > 0 && (
        <button className="inline-flex items-center gap-1 underline" onClick={() => void syncNow()}>
          <RefreshCw className="h-3.5 w-3.5" /> Send now
        </button>
      )}
      <Link to="/app/offline" className="underline">Offline records</Link>
    </div>
  )
}

/** Visit fields usable offline: departments and fees come from data already loaded in this session. */
export function VisitDraftFields({ value, onChange }: { value: VisitDraft; onChange: (v: VisitDraft) => void }) {
  const depts = useDepartments()
  const services = useServices()
  const clinical = (depts.data ?? []).filter((d) => d.kind === 'clinical' && d.code !== 'ER')
  const fees = (services.data ?? []).filter((s) => s.category === 'consultation' && (!value.department_id || s.department_id === value.department_id))
  if (!clinical.length) return <p className="text-[13px] text-amber-800">The department list is not available on this device yet, so the visit can be added after the record is sent.</p>
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <Select
        label="Department"
        value={value.department_id}
        placeholder="Choose…"
        onChange={(e) => {
          const d = clinical.find((x) => x.id === e.target.value)
          const fee = (services.data ?? []).find((s) => s.department_id === e.target.value && s.category === 'consultation')
          onChange({ ...value, department_id: e.target.value, department_name: d?.name ?? '', service_item_id: fee?.id ?? null })
        }}
        options={clinical.map((d) => ({ value: d.id, label: d.name }))}
      />
      <Select label="Fee" value={value.service_item_id ?? ''} placeholder="No charge" onChange={(e) => onChange({ ...value, service_item_id: e.target.value || null })} options={fees.map((s) => ({ value: s.id, label: `${s.name} — Rs. ${s.price}` }))} />
      <Input label="Reason for visit" value={value.visit_reason ?? ''} onChange={(e) => onChange({ ...value, visit_reason: e.target.value || null })} />
    </div>
  )
}

export const emptyVisit = (): VisitDraft => ({ department_id: '', department_name: '', visit_reason: null, service_item_id: null, payer_type: 'self', priority: 0 })
